import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  selectClient,
  selectMaybeClientPartner,
  accumulatePaymentOptionQuotes,
  clearAllPaymentOptionQuotes,
  setIsLoadingAllPaymentOptions,
} from '@/model';
import { quotesApi } from '@/api/waterlily';
import type { ShopInsuranceMode } from '@/features/shop-insurance';
import {
  buildQuoteQueryPayload,
  quoteQueryKey,
} from '@/features/shop-insurance/lib/build-quote-query-payload';
import type { Quote } from '@/model/quotesModel';

interface UseAllPaymentOptionQuotesFetchingProps {
  mode: ShopInsuranceMode;
  selectedTab: ShopInsuranceTab;
  hasPartnerWithDemographics: boolean;
}

export function useAllPaymentOptionQuotesFetching({
  mode,
  selectedTab,
  hasPartnerWithDemographics,
}: UseAllPaymentOptionQuotesFetchingProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const { flags } = useFeatureFlags();
  const fetchedKeyRef = useRef<string | null>(null);

  // Omitting cobPaymentOption causes the server to return ALL pay options in one call.
  const payload = useMemo(
    () =>
      mode === 'benefit-design'
        ? buildQuoteQueryPayload({
            client,
            clientPartner,
            policyType: selectedTab,
            hasPartnerWithDemographics,
            nycLifeDemo: flags.nycLifeDemo,
          })
        : null,
    [mode, client, clientPartner, selectedTab, hasPartnerWithDemographics, flags.nycLifeDemo],
  );

  // Same reasoning as useQuoteFetching: key the fetch on the serialized request, not on the
  // client object. Selecting or removing a product replaces state.client.data with a new
  // reference, and refetching there wipes allPaymentOptionQuotes — emptying the benefit-design
  // list and resetting scroll — even though every quote input is unchanged. clientId is part of
  // the key but not the request, so switching clients still refetches.
  const fetchKey = useMemo(() => {
    const requestKey = quoteQueryKey(payload);
    return requestKey && `${client?.clientId ?? ''}:${requestKey}`;
  }, [payload, client?.clientId]);

  useEffect(() => {
    if (!payload || !fetchKey || fetchedKeyRef.current === fetchKey) return;

    fetchedKeyRef.current = fetchKey;
    dispatch(clearAllPaymentOptionQuotes());
    dispatch(setIsLoadingAllPaymentOptions(true));

    let cancelled = false;
    let settled = false;

    const releaseKey = () => {
      if (fetchedKeyRef.current === fetchKey) {
        fetchedKeyRef.current = null;
      }
    };

    const fetchAll = async () => {
      try {
        const result = await quotesApi.query(payload);
        if (cancelled) return;
        settled = true;
        if (result?.quotes?.length) {
          dispatch(accumulatePaymentOptionQuotes(result.quotes as Quote[]));
        }
      } catch {
        // Non-fatal, but release the key so a later render can retry rather than leaving
        // benefit-design mode permanently empty.
        releaseKey();
      } finally {
        if (!cancelled) {
          dispatch(setIsLoadingAllPaymentOptions(false));
        }
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
      // StrictMode double-invokes effects and unmounts can interrupt a request. Either way the
      // quotes were never applied, so drop the key and let the next run fetch again.
      if (!settled) {
        releaseKey();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, dispatch]);

  return {};
}
